import {fetchJson} from '../../Library/Fetch.js';
import {cloneSpinner} from '../../Library/LoadingIndicator';

const FETCH_DEBOUNCE_MS = 250;
const LOCAL_SUGGESTION_LIMIT = 10;
const INPUT_SELECTOR = ':is(input, textarea)[data-suggestions-url], :is(input, textarea)[data-suggestions]';

const debounceTimers = new WeakMap();
const activeRequests = new WeakMap();
const pickedValues = new WeakMap();

const suggestionsUrl = (input, query) => {
    const separator = input.dataset.suggestionsUrl.includes('?') ? '&' : '?';

    return input.dataset.suggestionsUrl + separator + 'query=' + encodeURIComponent(query);
};

const listFor = input => {
    const sibling = input.nextElementSibling;

    return sibling?.matches('ul.input-suggestions') ? sibling : null;
};

const close = input => {
    clearTimeout(debounceTimers.get(input));
    activeRequests.delete(input);
    listFor(input)?.remove();
};

const closeAll = exceptInput => {
    document.querySelectorAll('ul.input-suggestions').forEach(list => {
        if (list.previousElementSibling !== exceptInput) {
            close(list.previousElementSibling);
        }
    });
};

const open = input => {
    let list = listFor(input);

    if (!list) {
        list = document.createElement('ul');
        list.className = 'input-suggestions';
        list.setAttribute('role', 'listbox');
        input.insertAdjacentElement('afterend', list);
    }

    list.style.left = `${input.offsetLeft}px`;
    list.style.width = `${input.offsetWidth}px`;

    return list;
};

const showLoading = input => {
    const loading = document.createElement('li');
    loading.className = 'input-suggestions-loading';
    loading.append(cloneSpinner());

    open(input).replaceChildren(loading);
};

const buildOption = ({value, label}) => {
    const option = document.createElement('li');
    option.setAttribute('role', 'option');
    option.dataset.value = value;
    option.textContent = value;

    if (label && label !== value) {
        const hint = document.createElement('span');
        hint.className = 'option-hint';
        hint.textContent = `(${label})`;
        option.append(hint);
    }

    return option;
};

const render = (input, suggestions) => {
    if (!suggestions.length) {
        close(input);

        return;
    }

    open(input).replaceChildren(...suggestions.map(buildOption));
};

const filterLocalSuggestions = (input, query) => {
    const matches = JSON.parse(input.dataset.suggestions).filter(({value, label}) => query === ''
        || value.toLocaleLowerCase().includes(query)
        || label.toLocaleLowerCase().includes(query),
    );

    render(input, matches.slice(0, LOCAL_SUGGESTION_LIMIT));
};

const showSuggestions = input => {
    if (input.dataset.suggestions !== undefined) {
        filterLocalSuggestions(input, input.value.trim().toLocaleLowerCase());

        return;
    }

    fetchSuggestions(input);
};

const fetchSuggestions = async input => {
    const token = Symbol('suggestions');
    activeRequests.set(input, token);
    showLoading(input);

    const payload = await fetchJson(suggestionsUrl(input, input.value.trim()));

    if (activeRequests.get(input) !== token) {
        return;
    }

    payload ? render(input, payload.suggestions) : close(input);
};

const select = (input, option) => {
    input.value = option.dataset.value;
    pickedValues.set(input, option.dataset.value);
    input.dispatchEvent(new Event('input', {bubbles: true}));
    input.dispatchEvent(new CustomEvent('suggestions:selected', {bubbles: true}));
    close(input);

    if (input.hasAttribute('data-suggestions-blur-on-select')) {
        input.blur();

        return;
    }

    input.focus();
};

const moveActive = (list, delta) => {
    const options = [...list.querySelectorAll('li[role=option]')];

    if (!options.length) {
        return;
    }

    const index = options.findIndex(option => option.classList.contains('is-active'));
    const next = index === -1
        ? options.at(delta > 0 ? 0 : -1)
        : options[(index + delta + options.length) % options.length];

    options[index]?.classList.remove('is-active');
    next.classList.add('is-active');
    next.scrollIntoView({block: 'nearest'});
};

export const handleSuggestionsInput = event => {
    const input = event.target;

    if (!input.matches?.(INPUT_SELECTOR)) {
        return;
    }

    clearTimeout(debounceTimers.get(input));

    if (pickedValues.get(input) === input.value) {
        return;
    }

    if (input.dataset.suggestions !== undefined) {
        filterLocalSuggestions(input, input.value.trim().toLocaleLowerCase());

        return;
    }

    debounceTimers.set(input, setTimeout(() => fetchSuggestions(input), FETCH_DEBOUNCE_MS));
};

export const handleSuggestionsFocus = event => {
    const input = event.target;

    if (!input.matches?.(INPUT_SELECTOR) || listFor(input)) {
        return;
    }

    if (pickedValues.get(input) === input.value) {
        return;
    }

    showSuggestions(input);
};

export const handleSuggestionsMousedown = event => {
    const option = event.target.closest?.('ul.input-suggestions li[role=option]');

    if (option) {
        event.preventDefault();
        select(option.parentElement.previousElementSibling, option);

        return;
    }

    const input = event.target.matches?.(INPUT_SELECTOR) ? event.target : null;

    closeAll(input);

    if (input && document.activeElement === input && !listFor(input)) {
        showSuggestions(input);
    }
};

export const handleSuggestionsKeydown = event => {
    const input = event.target;

    if (!input.matches?.(INPUT_SELECTOR)) {
        return false;
    }

    const list = listFor(input);

    if (!list) {
        return false;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        moveActive(list, event.key === 'ArrowDown' ? 1 : -1);

        return true;
    }

    if (event.key === 'Enter') {
        const active = list.querySelector('li.is-active');

        if (!active) {
            close(input);

            return false;
        }

        event.preventDefault();
        select(input, active);

        return true;
    }

    if (event.key === 'Escape') {
        event.stopImmediatePropagation();
        close(input);

        return true;
    }

    if (event.key === 'Tab') {
        close(input);
    }

    return false;
};
